import {
  Button,
  Col,
  Form,
  FormInstance,
  message,
  Row,
  Upload,
  Select as AntdSelect,
  Tooltip,
  Modal,
  Radio,
  Space,
} from "antd";
import React, { useEffect, useState } from "react";
import Text from "../../../components/Text/Text";
import styled from "styled-components";
import color from "../../../resource/color";
import { CalendarOutlined, DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import Input from "../../../components/Input/Input";
import Select from "../../../components/Select/Select";
import DatePicker, { TimePicker } from "../../../components/DatePicker/DatePicker";
import TextArea from "../../../components/Input/TextArea";
import dayjs, { Dayjs } from "dayjs";
import { PROMOTION_TYPE_NAME, PromotionType } from "../../../definitions/promotion";
import { checkPromotionCode, getPromotion } from "../../../datasource/PromotionDatasource";
import icon from "../../../resource/icon";
import { normFile } from "../../../utility/uploadHelper";
import { useRecoilValue } from "recoil";
import promotionState from "../../../store/promotion";
import { Document, pdfjs } from "react-pdf";
import { onInputNumberKeyPress } from "../../../utility/validator";

const MemoArea = styled.div`
  width: 100%;
  background: ${color.background1};
  border: 1px solid ${color.background2};
  border-radius: 8px;

  display: flex;
  align-items: center;
  padding: 16px;
`;

const MemoAreaItem = styled.div`
  padding: 0px 8px;
  display: flex;
  align-items: center;
`;

interface Props {
  form: FormInstance;
  fileMemo: Blob | undefined;
  setFileMemo: (file: Blob | undefined) => void;
  fileMemoUrl?: string;
  setFileMemoUrl?: (url: string | undefined) => void;
  isEditing?: boolean;
  isCopying?: boolean;
  imgPromotion?: boolean;
  setImgPromotion?: any;
  quotaUsed?: number;
  creditTermType?: string;
  form3?: FormInstance;
}
export const PromotionCreateStep1 = ({
  form,
  fileMemo,
  setFileMemo,
  fileMemoUrl,
  setFileMemoUrl,
  isEditing,
  isCopying,
  quotaUsed,
  creditTermType,
  form3,
}: Props) => {
  const userProfile = JSON.parse(localStorage.getItem("profile")!);
  const { company } = userProfile;
  const promoStateValue = useRecoilValue(promotionState);
  const creditTerm = Form.useWatch("creditTermType", form);
  const [promotions, setPromotions] = useState();
  const [promotionType, setPromotionType] = useState<PromotionType>(
    form.getFieldValue("promotionType"),
  );
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!isEditing || promoStateValue.promotion) {
      fetchPromotion();
      setPromotionType(form.getFieldValue("promotionType"));
    }
    setTimeout(() => {
      setLoading(false);
    }, 3000);
    pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
  }, [promoStateValue]);

  const fetchPromotion = async () => {
    try {
      const { data } = await getPromotion({ company });
      let promoList = data.map((p: any) => ({
        label: `${p.promotionName} (${p.promotionCode})`,
        value: `${p.promotionCode}`,
      }));
      if (isEditing) {
        const currentCode = promoStateValue.promotion?.promotionCode;
        promoList = promoList.filter((p: any) => p.value !== currentCode);
      }
      setPromotions(promoList);
    } catch (e) {
      console.log(e);
    }
  };

  const onValuesChange = (changedValues, allValues) => {
    // console.log(changedValues, allValues);
    if (changedValues.promotionType) {
      setPromotionType(changedValues.promotionType);
      form.setFieldValue("quota", undefined);
    }
  };

  return (
    <>
      <Form
        form={form}
        layout='vertical'
        initialValues={{ isShowSaleApp: true, isShowShopApp: true, isShowPromotion: true }}
        onValuesChange={onValuesChange}
      >
        <Row className='pb-3'>
          <Text level={5} fontWeight={700}>
            รายละเอียดข้อมูลเบื้องต้น
          </Text>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='promotionCode'
              label='รหัสโปรโมชัน'
              rules={[
                {
                  required: true,
                  message: "*โปรดระบุรหัสโปรโมชัน",
                },
                {
                  max: 20,
                  message: "*รหัสโปรโมชันต้องมีความยาวไม่เกิน 20 ตัวอักษร",
                },
                {
                  validator: async (rule, value) => {
                    if (isEditing) return;
                    const { success } = await checkPromotionCode({
                      promotionCode: value,
                      company,
                    });
                    if (!success) {
                      throw new Error();
                    }
                  },
                  message: "*รหัสโปรโมชันนี้ถูกใช้แล้ว",
                },
                {
                  pattern: /^[^*]*$/,
                  message: "*รหัสโปรโมชันต้องไม่มีเครื่องหมาย (*)",
                },
              ]}
            >
              <Input placeholder='ระบุรหัสโปรโมชัน' disabled={isEditing} />
            </Form.Item>
          </Col>
          <Col span={12} />
          <Col span={12}>
            <Form.Item
              name='promotionType'
              label='ประเภทโปรโมชัน'
              rules={[
                {
                  required: true,
                  message: "*โปรดเลือกประเภทโปรโมชัน",
                },
              ]}
            >
              <Select
                placeholder='เลือกประเภทโปรโมชัน'
                data={Object.entries(PROMOTION_TYPE_NAME).map(([key, label]) => ({
                  key: key,
                  value: key,
                  label: `${label}`,
                }))}
                onChange={() => form3?.resetFields()}
                disabled={isEditing || isCopying}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='quota'
              label='กำหนดโควตารวม'
              extra='หากไม่ระบุ ระบบจะไม่มีการจำกัดโควตา'
              rules={[
                {
                  validator(rule, value, callback) {
                    if (quotaUsed && quotaUsed > parseFloat(value)) {
                      return Promise.reject(
                        new Error(
                          `ไม่สามารถลดจำนวนโควตาที่ระบุได้ เนื่องจากร้านใช้โควตาไปแล้ว ${quotaUsed} ${
                            !promotionType
                              ? ""
                              : [
                                    PromotionType.DISCOUNT_MIX,
                                    PromotionType.DISCOUNT_NOT_MIX,
                                  ].includes(promotionType)
                                ? "บาท"
                                : "สิทธิ์"
                          }`,
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder='ระบุจำนวนโควตา'
                autoComplete='off'
                disabled={
                  (!promotionType && !isEditing && !isCopying) ||
                  promotionType === PromotionType.OTHER ||
                  form.getFieldValue("promotionType") === PromotionType.OTHER
                }
                suffix={
                  !promotionType
                    ? ""
                    : [PromotionType.DISCOUNT_MIX, PromotionType.DISCOUNT_NOT_MIX].includes(
                          promotionType,
                        )
                      ? "บาท"
                      : [PromotionType.DISCOUNT_MIX_RANGE].includes(promotionType)
                        ? "จำนวน"
                        : "สิทธิ์"
                }
                onKeyDown={
                  [
                    PromotionType.DISCOUNT_MIX,
                    PromotionType.DISCOUNT_NOT_MIX,
                    PromotionType.DISCOUNT_MIX_RANGE,
                  ].includes(promotionType as PromotionType)
                    ? onInputNumberKeyPress
                    : (e) => onInputNumberKeyPress(e, { onlyInt: true })
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='promotionName'
              label='ชื่อโปรโมชัน'
              rules={[
                {
                  required: true,
                  message: "*โปรดระบุชื่อโปรโมชัน",
                },
                {
                  max: 50,
                  message: "*ชื่อโปรโมชันต้องมีความยาวไม่เกิน 50 ตัวอักษร",
                },
              ]}
            >
              <Input placeholder='ระบุชื่อโปรโมชัน' autoComplete='off' />
            </Form.Item>
          </Col>
        </Row>
        <Row align='middle' gutter={16}>
          <Col span={12}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name='startDate'
                  label='วันที่เริ่มโปรโมชัน'
                  rules={[
                    {
                      required: true,
                      message: "*โปรดเลือกวันที่เริ่มโปรโมชัน",
                    },
                  ]}
                >
                  <DatePicker style={{ width: "100%" }} enablePast />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name='startTime'
                  label='เวลาเริ่มโปรโมชัน'
                  initialValue={dayjs("00:00", "HH:mm")}
                >
                  <TimePicker allowClear={false} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={12}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name='endDate'
                  label='วันที่สิ้นสุดโปรโมชัน'
                  rules={[
                    {
                      required: true,
                      message: "*โปรดเลือกวันที่สิ้นสุดโปรโมชัน",
                    },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    disabledDate={(current: Dayjs) => {
                      const startDate = form.getFieldValue("startDate");
                      return current && current.isBefore(dayjs(startDate));
                    }}
                    enablePast
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name='endTime'
                  label='เวลาสิ้นสุดโปรโมชัน'
                  initialValue={dayjs("23:59", "HH:mm")}
                >
                  <TimePicker
                    allowClear={false}
                    disabledTime={(now) => {
                      const startDate = form.getFieldValue("startDate") as Dayjs;
                      const endDate = form.getFieldValue("endDate") as Dayjs;
                      const startTime = form.getFieldValue("startTime") as Dayjs;
                      const isSameDay = startDate && endDate && startDate?.isSame(endDate, "year");
                      if (!isSameDay) return {};

                      const hour = startTime.hour();
                      const minute = startTime.minute();
                      const hours: number[] = [];
                      const minutes: number[] = [];
                      for (let i = 0; i < hour; i++) {
                        hours.push(i);
                      }
                      for (let i = 0; i <= minute; i++) {
                        minutes.push(i);
                      }
                      return {
                        disabledHours: () => hours,
                        disabledMinutes: (selectedHour: number) =>
                          selectedHour === hour ? minutes : [],
                      };
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
        <Form.Item
          name='creditTermType'
          label='การกำหนด Credit Term'
          rules={[
            {
              required: true,
              message: "*โปรดเลือก Credit Term",
            },
          ]}
        >
          <Radio.Group
            style={{ width: "100%" }}
            onChange={(e) => {
              form.setFieldValue("creditTermValue", null);
            }}
            value={creditTermType}
          >
            <Space direction='vertical' style={{ width: "20%" }}>
              <div>
                <Radio value='DAY'>
                  <Text level={6} fontFamily='Sarabun'>
                    กำหนดจากจำนวนวัน
                  </Text>
                </Radio>
                {creditTerm === "DAY" && (
                  <Form.Item
                    name='creditTermValue'
                    style={{ marginTop: 8, marginLeft: 24, marginBottom: 0, fontFamily: "Sarabun" }}
                    rules={[
                      {
                        required: true,
                        message: "*โปรดระบุจำนวนวัน",
                      },
                    ]}
                  >
                    <Input
                      placeholder='ระบุจำนวนวัน'
                      suffix={
                        <Text fontFamily='Sarabun' fontSize={14}>
                          วัน
                        </Text>
                      }
                      autoComplete='off'
                      onKeyDown={onInputNumberKeyPress}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                )}
              </div>

              <div>
                <Radio value='DATE'>
                  <Text level={6} fontFamily='Sarabun'>
                    กำหนดจากวันที่
                  </Text>
                </Radio>
                {creditTerm === "DATE" && (
                  <Form.Item
                    name='creditTermValue'
                    style={{ marginTop: 8, marginLeft: 24 }}
                    rules={[
                      {
                        required: true,
                        message: "*โปรดเลือกวันที่",
                      },
                    ]}
                  >
                    <DatePicker suffixIcon={<CalendarOutlined />} enablePast />
                  </Form.Item>
                )}
              </div>

              <Radio value='NONE'>
                <Text level={6} fontFamily='Sarabun'>
                  ไม่กำหนดเครดิต
                </Text>
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
        <Row align='middle'>
          <Text level={5} fontWeight={700}>
            ไฟล์ Memo Promotion
          </Text>
          &nbsp;
          <Tooltip title='ไฟล์รูปแบบ PDF ที่เป็นต้นฉบับของโปรโมชัน ไว้สำหรับอ้างอิงดูเอกสารต้นฉบับผ่านทางหน้าแอปพลิเคชัน Sellcoda Sale'>
            <img style={{ width: 16, height: 16 }} src={icon.infoIcon} />
          </Tooltip>
        </Row>
        <br />
        <Text level={5} color='Text3'>
          โปรดเลือกไฟล์ Memo Promotion ที่ต้องการแสดงในหน้า Application
        </Text>
        <br />
        <Row>
          <Col span={12}>
            <MemoArea>
              <MemoAreaItem style={{ width: 124 }}>
                <Form.Item
                  noStyle
                  name='memoFile'
                  valuePropName='fileList'
                  getValueFromEvent={normFile}
                >
                  <Upload
                    beforeUpload={(file) => {
                      const isPDF = file.type === "application/pdf";
                      if (!isPDF) {
                        message.error(`อัปโหลดเฉพาะไฟล์ pdf เท่านั้น`);
                        return false;
                      }
                      return isPDF || Upload.LIST_IGNORE;
                    }}
                    customRequest={({ file, onSuccess }) => {
                      if (onSuccess) {
                        onSuccess(file);
                      }
                    }}
                    onChange={({ file }: any) => {
                      if (file.status === "uploading") {
                        setFileMemo(file);
                        file.status = "done";
                      }
                      if (file.status === "done") {
                        setFileMemo(file);
                      }
                      return "success";
                    }}
                    onRemove={() => {
                      setFileMemo(undefined);
                    }}
                    maxCount={1}
                    showUploadList={false}
                  >
                    <Button type='primary' icon={<UploadOutlined />}>
                      เลือกไฟล์
                    </Button>
                  </Upload>
                </Form.Item>
              </MemoAreaItem>
              <MemoAreaItem style={{ width: "calc(100% - 156px)" }}>
                <svg
                  width='24'
                  height='31'
                  viewBox='0 0 24 31'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M20.0596 0.0175781L24 3.84855V5.85091V27.5176C24 28.8983 22.8487 30.0176 21.4286 30.0176H2.57143C1.15127 30.0176 0 28.8983 0 27.5176V2.51758C0 1.13687 1.15127 0.0175781 2.57143 0.0175781H18H20.0596ZM18 1.18424H2.57143C1.85609 1.18424 1.26867 1.71671 1.2056 2.39622L1.2 2.51758V27.5176C1.2 28.213 1.74768 28.7842 2.4466 28.8455L2.57143 28.8509H21.4286C22.1439 28.8509 22.7313 28.3184 22.7944 27.6389L22.8 27.5176V5.85091H18V1.18424ZM22.8 4.68424L22.8 4.33174L19.5626 1.18424H19.2V4.68424H22.8Z'
                    fill='#464E5F'
                  />
                  <path d='M4 15.6846H5.83333V17.5179H4V15.6846Z' fill='#464E5F' />
                  <path
                    d='M6.75 12.0176H8.98014C9.41286 12.0176 9.74294 12.0895 9.97039 12.2334C10.2034 12.372 10.3587 12.5718 10.4364 12.833C10.5196 13.0888 10.5612 13.4246 10.5612 13.8403C10.5612 14.24 10.5224 14.5704 10.4447 14.8315C10.367 15.0873 10.2117 15.2925 9.97871 15.4471C9.74571 15.5963 9.40731 15.6709 8.9635 15.6709H7.80682V17.5176H6.75V12.0176ZM8.614 14.7916C8.89138 14.7916 9.08832 14.7676 9.20482 14.7196C9.32687 14.6663 9.40454 14.5784 9.43782 14.4558C9.47665 14.3279 9.49607 14.1227 9.49607 13.8403C9.49607 13.5578 9.47665 13.3553 9.43782 13.2327C9.40454 13.1048 9.32964 13.0169 9.21314 12.9689C9.09664 12.9209 8.90248 12.8969 8.63064 12.8969H7.80682V14.7916H8.614Z'
                    fill='#464E5F'
                  />
                  <path
                    d='M11.4471 12.0176H13.1696C13.8741 12.0176 14.3734 12.0949 14.6674 12.2494C14.967 12.3986 15.1529 12.6491 15.225 13.0009C15.3027 13.3473 15.3415 13.9362 15.3415 14.7676C15.3415 15.599 15.3027 16.1905 15.225 16.5423C15.1529 16.8887 14.967 17.1392 14.6674 17.2937C14.3734 17.443 13.8741 17.5176 13.1696 17.5176H11.4471V12.0176ZM13.1197 16.6382C13.5246 16.6382 13.7965 16.6089 13.9352 16.5503C14.0794 16.4917 14.1709 16.3451 14.2098 16.1106C14.2542 15.8761 14.2763 15.4284 14.2763 14.7676C14.2763 14.1067 14.2542 13.6591 14.2098 13.4246C14.1709 13.1901 14.0794 13.0435 13.9352 12.9849C13.7965 12.9263 13.5246 12.8969 13.1197 12.8969H12.5039V16.6382H13.1197Z'
                    fill='#464E5F'
                  />
                  <path
                    d='M16.3879 17.5176V12.0176H19.5833V12.8969H17.4447V14.4318H19.3337V15.3192H17.4447V17.5176H16.3879Z'
                    fill='#464E5F'
                  />
                </svg>
                &nbsp;
                <Text
                  level={6}
                  color='Text3'
                  onClick={async () => {
                    try {
                      if (fileMemo && (fileMemo as any)?.originFileObj) {
                        const file = new Blob([(fileMemo as any)?.originFileObj], {
                          type: "application/pdf",
                        });
                        const fileURL = URL.createObjectURL(file);
                        const pdfWindow = window.open();
                        if (pdfWindow) pdfWindow.location.href = fileURL || "";
                      } else if (fileMemoUrl) {
                        const pdfWindow = window.open();
                        if (pdfWindow) pdfWindow.location.href = fileMemoUrl || "";
                      }
                    } catch (error) {
                      return { error };
                    }
                  }}
                  style={{ cursor: fileMemo || fileMemoUrl ? "pointer" : "default" }}
                >
                  {(fileMemo as any)?.originFileObj?.name ||
                    (fileMemoUrl && `${fileMemoUrl?.substring(0, 20)}...`) ||
                    "โปรดเลือกไฟล์ .PDF"}
                </Text>
              </MemoAreaItem>
              <MemoAreaItem
                style={{
                  width: 32,
                  visibility:
                    (fileMemo as any)?.originFileObj || fileMemoUrl ? "visible" : "hidden",
                }}
              >
                <DeleteOutlined
                  style={{ color: color.error, cursor: "pointer" }}
                  onClick={() => {
                    Modal.confirm({
                      title: "ลบไฟล์ Memo Promotion",
                      onOk: () => {
                        setFileMemo(undefined);
                        if (setFileMemoUrl) setFileMemoUrl(undefined);
                      },
                    });
                  }}
                />
              </MemoAreaItem>
            </MemoArea>
          </Col>
        </Row>
        <br />
        <br />
        <Text level={5} fontWeight={700}>
          อ้างอิงเลขที่โปรโมชันที่เกี่ยวข้อง
        </Text>
        <br />
        <Text level={5} color='Text3'>
          โปรดเลือกเลขที่โปรโมชันอ้างอิงโปรโมชันที่เกี่ยวข้อง สามารถเลือกได้มากกว่า 1 โปรโมชัน
        </Text>
        <br />
        <br />
        <Row>
          <Col span={12}>
            <Form.Item name='referencePromotion'>
              <AntdSelect
                mode='multiple'
                placeholder='เลือกโปรโมชันอ้างอิงโปรโมชันที่เกี่ยวข้อง'
                onChange={() => {
                  console.log();
                }}
                style={{ width: "100%", lineHeight: "40px" }}
                options={promotions || []}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item name='comment' label='หมายเหตุเพิ่มเติม'>
              <TextArea />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  );
};
